import { useEffect, useState } from "react";
import API_BASE from "../../../../config";
import { authHeaders, canEditParent } from "../profileUtils";
import s from "./FamilyTab.module.css";

const RELATIONSHIP_OPTIONS = [
  "Mother", "Father", "Guardian", "Grandmother", "Grandfather",
  "Aunt", "Uncle", "Sibling", "Step-Mother", "Step-Father", "Other",
];

export default function FamilyTab({ studentId }) {
  const [parents,  setParents]  = useState([]);
  const [links,    setLinks]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState("");

  /* id → { form, saving, error } for cards in edit mode */
  const [editState, setEditState] = useState({});

  const canEdit = canEditParent();

  useEffect(() => {
    if (!studentId) return;
    let cancelled = false;
    setLoading(true); setError("");

    Promise.all([
      fetch(`${API_BASE}/api/parent-student?studentId=${studentId}`, { headers: authHeaders() })
        .then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/parents`, { headers: authHeaders() })
        .then(r => r.ok ? r.json() : []),
    ])
      .then(([rawLinks, allParents]) => {
        if (cancelled) return;
        const linksArr  = Array.isArray(rawLinks)   ? rawLinks   : [];
        const allArr    = Array.isArray(allParents) ? allParents : [];
        const linkedIds = new Set(linksArr.map(l => l.parentId));
        setLinks(linksArr);
        setParents(
          allArr
            .filter(p => linkedIds.has(p.id))
            .sort((a, b) => {
              const lA = linksArr.find(l => l.parentId === a.id);
              const lB = linksArr.find(l => l.parentId === b.id);
              if ((lA?.primary ? 0 : 1) !== (lB?.primary ? 0 : 1))
                return (lA?.primary ? 0 : 1) - (lB?.primary ? 0 : 1);
              return ((a.lastName || "") + (a.firstName || "")).localeCompare(
                     (b.lastName || "") + (b.firstName || ""));
            })
        );
      })
      .catch(() => { if (!cancelled) setError("Could not load family information."); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [studentId]);

  function startEdit(p, link) {
    setEditState(prev => ({
      ...prev,
      [p.id]: {
        form: {
          firstName:    p.firstName    || "",
          lastName:     p.lastName     || "",
          contact:      p.contact      || "",
          email:        p.email        || "",
          occupation:   p.occupation   || "",
          address:      p.address      || "",
          relationship: link?.relationship || "Parent",
          primary:      !!link?.primary,
        },
        saving: false,
        error:  "",
      },
    }));
  }

  function cancelEdit(id) {
    setEditState(prev => { const next = { ...prev }; delete next[id]; return next; });
  }

  function setField(id, field, value) {
    setEditState(prev => ({
      ...prev,
      [id]: { ...prev[id], form: { ...prev[id].form, [field]: value } },
    }));
  }

  async function saveCard(p, link) {
    const es = editState[p.id];
    if (!es) return;
    setEditState(prev => ({ ...prev, [p.id]: { ...prev[p.id], saving: true, error: "" } }));

    try {
      const [parentRes, linkRes] = await Promise.all([
        fetch(`${API_BASE}/api/parents/${p.id}`, {
          method: "PUT",
          headers: { ...authHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({
            firstName:  es.form.firstName,
            lastName:   es.form.lastName,
            contact:    es.form.contact,
            email:      es.form.email,
            occupation: es.form.occupation,
            address:    es.form.address,
          }),
        }),
        link
          ? fetch(`${API_BASE}/api/parent-student/${link.id}`, {
              method: "PUT",
              headers: { ...authHeaders(), "Content-Type": "application/json" },
              body: JSON.stringify({
                relationship: es.form.relationship,
                primary:      es.form.primary,
              }),
            })
          : Promise.resolve(null),
      ]);

      if (!parentRes.ok) throw new Error(`Parent save failed (HTTP ${parentRes.status})`);
      if (linkRes && !linkRes.ok) throw new Error(`Link save failed (HTTP ${linkRes.status})`);

      const updatedParent = await parentRes.json();
      const updatedLink   = linkRes ? await linkRes.json() : link;

      setParents(prev => prev.map(px => px.id === p.id ? updatedParent : px));
      if (updatedLink) setLinks(prev => prev.map(lx => lx.id === link.id ? updatedLink : lx));
      cancelEdit(p.id);
    } catch (err) {
      setEditState(prev => ({
        ...prev,
        [p.id]: { ...prev[p.id], saving: false, error: err.message || "Could not save. Please try again." },
      }));
    }
  }

  if (loading) return <TabSpinner label="Loading family information…" />;
  if (error)   return <TabError message={error} />;
  if (parents.length === 0) return (
    <TabEmpty
      icon="👨‍👩‍👧"
      title="No guardians on record"
      sub="Parents and guardians can be linked from the Parent ↔ Student tab in the Directory."
    />
  );

  return (
    <div className={s.root}>
      <div className={s.countLine}>
        {parents.length} guardian{parents.length !== 1 ? "s" : ""} on record
      </div>
      <div className={s.cardGrid}>
        {parents.map((p, idx) => {
          const link    = links.find(l => l.parentId === p.id);
          const rel     = link?.relationship || "Parent";
          const primary = !!link?.primary;
          const name    = [p.lastName, p.firstName].filter(Boolean).join(", ") || "(Unnamed)";
          const es      = editState[p.id];

          if (es) {
            /* ── Edit card ────────────────────────────────────────── */
            return (
              <div key={p.id} className={`${s.card} ${primary ? s.cardPrimary : ""}`}
                style={{ animationDelay: `${idx * 60}ms` }}>
                <div className={s.cardHead}>
                  <div className={s.cardName}>{name}</div>
                  <div className={s.cardEditActions}>
                    <button className={s.cancelBtn} onClick={() => cancelEdit(p.id)} disabled={es.saving}>
                      Cancel
                    </button>
                    <button className={s.saveBtn} onClick={() => saveCard(p, link)} disabled={es.saving}>
                      {es.saving ? "Saving…" : "Save"}
                    </button>
                  </div>
                </div>
                {es.error && <div className={s.cardError}>{es.error}</div>}
                <div className={s.cardBody}>
                  <div className={s.editRow}>
                    <div className={s.editField}>
                      <label className={s.editLabel}>Last Name</label>
                      <input className={s.editInput} value={es.form.lastName}
                        onChange={e => setField(p.id, "lastName", e.target.value)} />
                    </div>
                    <div className={s.editField}>
                      <label className={s.editLabel}>First Name</label>
                      <input className={s.editInput} value={es.form.firstName}
                        onChange={e => setField(p.id, "firstName", e.target.value)} />
                    </div>
                  </div>
                  <div className={s.editRow}>
                    <div className={s.editField}>
                      <label className={s.editLabel}>Relationship</label>
                      <select className={s.editSelect} value={es.form.relationship}
                        onChange={e => setField(p.id, "relationship", e.target.value)}>
                        {RELATIONSHIP_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                    <div className={s.editField}>
                      <label className={s.editLabel}>Contact</label>
                      <input className={s.editInput} value={es.form.contact}
                        onChange={e => setField(p.id, "contact", e.target.value)} />
                    </div>
                  </div>
                  <div className={s.editField}>
                    <label className={s.editLabel}>Email</label>
                    <input type="email" className={s.editInput} value={es.form.email}
                      onChange={e => setField(p.id, "email", e.target.value)} />
                  </div>
                  <div className={s.editField}>
                    <label className={s.editLabel}>Occupation</label>
                    <input className={s.editInput} value={es.form.occupation}
                      onChange={e => setField(p.id, "occupation", e.target.value)} />
                  </div>
                  <div className={s.editField}>
                    <label className={s.editLabel}>Address</label>
                    <textarea className={s.editTextarea} value={es.form.address} rows={2}
                      onChange={e => setField(p.id, "address", e.target.value)} />
                  </div>
                  <label className={s.primaryCheck}>
                    <input type="checkbox" checked={es.form.primary}
                      onChange={e => setField(p.id, "primary", e.target.checked)} />
                    Primary guardian
                  </label>
                </div>
              </div>
            );
          }

          /* ── Read card ──────────────────────────────────────────── */
          return (
            <div key={p.id} className={`${s.card} ${primary ? s.cardPrimary : ""}`}
              style={{ animationDelay: `${idx * 60}ms` }}>
              <div className={s.cardHead}>
                <div className={s.cardName}>{name}</div>
                <div className={s.cardHeadRight}>
                  <span className={`${s.relBadge} ${primary ? s.relBadgePrimary : ""}`}>
                    {primary ? `${rel} · Primary` : rel}
                  </span>
                  {canEdit && (
                    <button className={s.editBtn} type="button" onClick={() => startEdit(p, link)}>
                      Edit
                    </button>
                  )}
                </div>
              </div>
              <div className={s.cardBody}>
                {p.contact && (
                  <div className={s.detail}>
                    <span className={s.detailIcon} aria-hidden="true">📞</span>
                    <span>{p.contact}</span>
                  </div>
                )}
                {p.email && (
                  <div className={s.detail}>
                    <span className={s.detailIcon} aria-hidden="true">✉</span>
                    <a href={`mailto:${p.email}`} className={s.emailLink} rel="noreferrer">{p.email}</a>
                  </div>
                )}
                {p.occupation && (
                  <div className={s.detail}>
                    <span className={s.detailIcon} aria-hidden="true">💼</span>
                    <span>{p.occupation}</span>
                  </div>
                )}
                {p.address && (
                  <div className={s.detail}>
                    <span className={s.detailIcon} aria-hidden="true">🏠</span>
                    <span>{p.address}</span>
                  </div>
                )}
                {!p.contact && !p.email && !p.occupation && !p.address && (
                  <div className={s.noDetails}>No contact details on record.</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Shared sub-components used across tabs ─────────────────────────── */
export function TabSpinner({ label = "Loading…" }) {
  return (
    <div className={s.spinnerWrap}>
      <div className={s.spinner} aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}
export function TabError({ message }) {
  return <div className={s.error}>{message}</div>;
}
export function TabEmpty({ icon, title, sub }) {
  return (
    <div className={s.empty}>
      {icon && <div className={s.emptyIcon} aria-hidden="true">{icon}</div>}
      <div className={s.emptyTitle}>{title}</div>
      {sub && <div className={s.emptySub}>{sub}</div>}
    </div>
  );
}
